import { withTopbarProps } from '@jusbrasil/topbar'
import { withUserSession } from '@jusbrasil-web/shared-user-session'
import { sendMetrics } from '@jusbrasil-web/shared-utils-events'
import { getFirstQueryParam } from '@jusbrasil-web/shared-utils-url'
import { GetServerSideProps } from 'next'
import { useRouter } from 'next/router'
import { useCallback, useEffect } from 'react'

import { internalAuthApi } from '../../api'
import { AuthLayout, PasswordSignIn } from '../../components'
import { JUSID_PLATFORM_WEB_AUTH, MODAL_EVENTS, PRODUCTION_ORIGIN, ROUTES } from '../../constants'
import { useLogin } from '../../hooks/useLogin'

export interface LoginDetailsPageProps {
  email?: string
  appId?: string
  featureCode?: string
  fromComponent?: string
  nextUrl?: string
  csrfToken?: string | null
}

export function LoginDetailsPage({
  email: emailProp,
  nextUrl: nextUrlProp,
  appId,
  featureCode,
  fromComponent,
}: LoginDetailsPageProps) {
  const router = useRouter()
  const email = emailProp || (router.query.email as string)
  const nextUrl = nextUrlProp || (router.query.next_url as string)

  const { isLoading, error, handlePasswordSubmit } = useLogin({
    appId,
    featureCode,
    fromComponent,
    nextUrl,
    initialEmail: email,
  })

  const handleBackToEmail = useCallback(() => {
    router.push({ pathname: ROUTES.SIGN_IN.BEGIN, query: router.query })
  }, [router])

  const handleForgotPassword = useCallback(() => {
    router.push({ pathname: ROUTES.PASSWORD_RECOVERY.BEGIN, query: { email } })
  }, [router, email])

  useEffect(() => {
    if (!email) {
      router.push({ pathname: ROUTES.SIGN_IN.BEGIN, query: router.query })
    }
  }, [email, router])

  const handleCloseClick = useCallback(() => {
    const params: Record<string, string> = {
      app_id: appId ?? JUSID_PLATFORM_WEB_AUTH,
      email,
      jusid_platform: JUSID_PLATFORM_WEB_AUTH,
      type: 'LogInModal',
    }
    if (featureCode) params.feature_code = featureCode
    if (fromComponent) params.from_component = fromComponent
    sendMetrics(MODAL_EVENTS.CLOSED, null, params)
    window.location.href = PRODUCTION_ORIGIN
  }, [email, appId, featureCode, fromComponent])

  if (!email) {
    return null
  }

  return (
    <AuthLayout>
      <PasswordSignIn
        email={email}
        isLoading={isLoading}
        error={error}
        onSubmit={handlePasswordSubmit}
        onBackToEmail={handleBackToEmail}
        onForgotPassword={handleForgotPassword}
        onCloseClick={handleCloseClick}
      />
    </AuthLayout>
  )
}

const getServerSideProps: GetServerSideProps = async (ctx) => {
  const { csrfToken } = await internalAuthApi.getCsrfToken(ctx)

  return {
    props: {
      ...withTopbarProps(ctx),
      ...withUserSession(ctx),
      email: getFirstQueryParam(ctx.query.email) || null,
      appId: getFirstQueryParam(ctx.query.app_id) || null,
      featureCode: getFirstQueryParam(ctx.query.feature_code) || null,
      fromComponent: getFirstQueryParam(ctx.query.from_component) || null,
      nextUrl: getFirstQueryParam(ctx.query.next_url) || null,
      csrfToken,
    },
  }
}

export const LoginDetailsPageExports = {
  getServerSideProps,
}
